import SEO from "../components/SEO";

const Terms = () => (
    <div className="pt-32 pb-20 max-w-4xl mx-auto px-6 prose prose-orange">
        <SEO
            title="Términos y Condiciones | Planix"
            description="Condiciones de uso para planos arquitectónicos, diseños digitales y servicios técnicos ofrecidos por Planix."
            canonical="https://www.planixapp.com/terminos-y-condiciones"
            keywords="términos y condiciones planos, uso de diseños digitales, responsabilidad del usuario, asesoría profesional obligatoria, limitación de responsabilidad Planix"
            image="https://www.planixapp.com/cover.jpg"
            structuredData={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Términos y Condiciones",
                "url": "https://www.planixapp.com/terminos-y-condiciones"
            }}
        />

        <div className="pt-32 pb-20 max-w-4xl mx-auto px-6">
            <div className="bg-gray-50 p-8 md:p-12 rounded-[2.5rem] border border-gray-100 shadow-sm">
                <h1 className="text-4xl font-black text-[#1C1C1C] mb-6 tracking-tighter">
                    Términos y <span className="text-[#F26B1D]">Condiciones</span>
                </h1>
                <p className="text-gray-500 mb-10 text-sm italic">Última actualización: Abril 2026</p>

                <div className="space-y-10 text-gray-600 leading-relaxed text-sm md:text-base">

                    {/* SECCIÓN CRÍTICA: NATURALEZA DEL CONTENIDO */}
                    <section>
                        <h2 className="text-xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3">
                            <span className="bg-[#F26B1D] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px]">01</span>
                            Naturaleza del Contenido
                        </h2>
                        <p>
                            Planix funciona como una plataforma de <strong>curaduría y referencia visual de arquitectura</strong>. Los planos, diseños y modelos presentados provienen de diversas fuentes y tendencias globales de arquitectura. El usuario acepta que el material adquirido es estrictamente <strong>referencial e ilustrativo</strong>.
                        </p>
                    </section>

                    {/* SECCIÓN CRÍTICA: ASESORÍA OBLIGATORIA */}
                    <section className="bg-[#F26B1D]/5 p-6 rounded-2xl border-l-4 border-[#F26B1D]">
                        <h2 className="text-xl font-black text-[#1C1C1C] mb-4">
                            02. Asesoría Profesional Obligatoria
                        </h2>
                        <p className="font-bold text-[#1C1C1C] mb-3">
                            ES REQUISITO OBLIGATORIO E INDISPENSABLE:
                        </p>
                        <ul className="list-disc ml-5 space-y-2">
                            <li>Que el usuario contrate a un <strong>Ingeniero Civil o Arquitecto colegiado</strong> en su localidad antes de iniciar cualquier construcción.</li>
                            <li>Realizar un estudio de suelos y cálculo estructural específico para el terreno de destino.</li>
                            <li>Adaptar el diseño a las normativas municipales y reglamentos de edificación vigentes en su ciudad.</li>
                        </ul>
                    </section>

                    {/* SECCIÓN: LIMITACIÓN DE RESPONSABILIDAD */}
                    <section>
                        <h2 className="text-xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3">
                            <span className="bg-[#1C1C1C] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px]">03</span>
                            Limitación de Responsabilidad
                        </h2>
                        <p>
                            Planix no se hace responsable por fallas estructurales, errores de construcción, colapsos o multas municipales derivadas del uso de estos planos de referencia. Al ser material de <strong>inspiración</strong>, el usuario asume la responsabilidad total de la ejecución técnica de la obra de la mano con su profesional de confianza.
                        </p>
                    </section>

                    {/* SECCIÓN: PROPIEDAD Y USO */}
                    <section>
                        <h2 className="text-xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3">
                            <span className="bg-[#1C1C1C] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px]">04</span>
                            Licencia de Uso y Adaptación
                        </h2>
                        <p>
                            La compra del archivo otorga al usuario la potestad de <strong>modificar, ampliar o adaptar</strong> el diseño a sus necesidades. Sin embargo, esta licencia es para uso personal; queda prohibida la venta masiva de estos archivos bajo el nombre de Planix o cualquier otra marca.
                        </p>
                    </section>

                    <div className="mt-12 p-6 bg-gray-100 rounded-2xl">
                        <p className="text-[11px] font-bold text-gray-500 uppercase text-center tracking-widest">
                            Al proceder con la compra en Planix, usted declara haber leído y aceptado que el contenido es referencial y requiere validación profesional.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        {/* SECCIÓN: PROCESOS CONSTRUCTIVOS Y PERSONAL */}
        <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <h2 className="text-xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3">
                <span className="bg-[#1C1C1C] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px]">05</span>
                Ejecución y Procesos Constructivos
            </h2>
            <div className="space-y-4 text-gray-600">
                <p>
                    Para la correcta ejecución de cualquier proyecto basado en nuestros planos de referencia, el usuario se compromete a realizar los <strong>procesos técnicos preliminares</strong>, tales como:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <li className="flex items-start gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <div className="w-1.5 h-1.5 bg-[#F26B1D] rounded-full mt-1.5 shrink-0" />
                        <span className="text-xs">Estudio de mecánica de suelos (indispensable para la cimentación).</span>
                    </li>
                    <li className="flex items-start gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <div className="w-1.5 h-1.5 bg-[#F26B1D] rounded-full mt-1.5 shrink-0" />
                        <span className="text-xs">Levantamiento topográfico y verificación de linderos.</span>
                    </li>
                    <li className="flex items-start gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <div className="w-1.5 h-1.5 bg-[#F26B1D] rounded-full mt-1.5 shrink-0" />
                        <span className="text-xs">Cálculo de instalaciones sanitarias y eléctricas locales.</span>
                    </li>
                </ul>

                <div className="mt-4 p-4 border-l-4 border-blue-500 bg-blue-50">
                    <p className="text-sm font-bold text-[#1C1C1C] mb-2 uppercase">Supervisión en Obra:</p>
                    <p className="text-sm">
                        Planix recomienda que toda obra cuente con la <strong>aprobación y supervisión constante de un Maestro de Obra con experiencia</strong>, quien trabajará en conjunto con el Ingeniero Residente para asegurar que los procesos constructivos respeten las buenas prácticas de ingeniería y seguridad.
                    </p>
                </div>
            </div>
        </section>
    </div>
);

export default Terms;